import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
  ToolbarActionConfig,
} from '@/blocks/object-toolbar';

/**
 * Assemble the object-toolbar config for the Attestations register/list pages
 * (CUS-176). Register toolbars are type-level: no `parentObject` (permissions
 * resolve against the type) and no `save` (a list page has no form). The
 * register's multi-format export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 * Attestations' register views (All / By user / Cycles / per-user cards) are
 * export-only, so this never supplies `create`.
 *
 * Takes the shared {@link RegisterToolbarConfigArgs} (see
 * `blocks/object-toolbar/types.ts`). Pure so it can be unit-tested without
 * rendering the page.
 */
export const buildAttestationRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  editRibbon,
  primaryActions,
}: RegisterToolbarConfigArgs & {
  /**
   * Attestation-specific inline primary actions. The per-user cards view
   * relocates its bulk "Attestation Not Required" opener here (CUS-176) — the
   * stateful modal stays mounted in the page body; this action just opens it.
   */
  primaryActions?: ToolbarActionConfig[];
}): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(primaryActions && primaryActions.length > 0
      ? { primary: primaryActions }
      : {}),
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
